/**
 * Music Preferences Service (Web)
 *
 * Fetches and caches music genres, moods, and tempos from the backend API.
 * Used in the onboarding wizard step 2/5 (Music Selection).
 */
import {
  MusicPreferencesResponse,
  CachedMusicPreferences,
} from '../types/music';
import { getCurrentLanguage } from '@/i18n/i18nCore';
import { getApiUrl } from '../../lib/runtimeConfig';

// Re-export types for convenience
export type { MusicPreferencesResponse, GenreDto, MoodDto, TempoDto } from '../types/music';

const API_URL = getApiUrl() || 'http://localhost:5001';

/** localStorage key for music preferences cache */
const CACHE_KEY = 'streamko_music_preferences';

/** Cache TTL: 24 hours in milliseconds */
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Gets the current language code from the i18n provider.
 * Falls back to browser language or 'en'.
 */
function getLanguageCode(): string {
  // Get from i18n provider
  return getCurrentLanguage();
}

/**
 * Checks if the cached data is still valid.
 */
function isCacheValid(cached: CachedMusicPreferences, languageCode: string): boolean {
  if (cached.languageCode !== languageCode) {
    return false;
  }
  const cachedTime = new Date(cached.cachedAt).getTime();
  return Date.now() - cachedTime < CACHE_TTL_MS;
}

/**
 * Reads cached preferences from localStorage.
 */
function readCache(): CachedMusicPreferences | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CachedMusicPreferences;
  } catch {
    return null;
  }
}

/**
 * Writes preferences to localStorage cache.
 */
function writeCache(data: MusicPreferencesResponse, languageCode: string): void {
  try {
    const cached: CachedMusicPreferences = {
      data,
      cachedAt: new Date().toISOString(),
      languageCode,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Silently fail if localStorage is full or unavailable
  }
}

/**
 * Fetches music preferences from the API.
 * Uses cache-first strategy with 24h TTL.
 *
 * @param forceRefresh - Bypass cache and fetch fresh data
 * @returns Music preferences response
 */
export async function getMusicPreferences(
  forceRefresh: boolean = false,
): Promise<MusicPreferencesResponse> {
  const languageCode = getLanguageCode();

  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = readCache();
    if (cached && isCacheValid(cached, languageCode)) {
      return cached.data;
    }
  }

  const response = await fetch(`${API_URL}/api/v1/music/preferences`, {
    method: 'GET',
    headers: {
      'Accept-Language': languageCode,
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch music preferences: ${response.status}`);
  }

  const data: MusicPreferencesResponse = await response.json();
  writeCache(data, languageCode);
  return data;
}

/**
 * Attempts to get music preferences, falling back to stale cache on error.
 *
 * @returns Music preferences (fresh or stale cached)
 */
export async function getMusicPreferencesWithFallback(): Promise<MusicPreferencesResponse> {
  try {
    return await getMusicPreferences();
  } catch {
    const cached = readCache();
    if (cached) {
      return cached.data;
    }
    throw new Error('No music preferences available (network error, no cache)');
  }
}

/**
 * Clears the music preferences cache.
 */
export function clearMusicPreferencesCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // Silently fail
  }
}
